import { useCallback, useEffect, useState } from 'react';
import moment, { Moment } from 'moment';

import type { DateFilterOperator } from '@/components/AgGrid/components/AgDateFilter/operators';
import type { AgDateCondition } from '@/components/AgGrid/components/AgDateFilter/types';

type Params = {
  defaultCondition: DateFilterOperator;
  defaultFromDate?: string | null;
  defaultToDate?: string | null;
};

type PickedDate = Moment | Date | null;

// The first render skips an empty date; a reset keeps one as an (invalid) moment, as the filter always did.
const toMoment = (date?: string | null): Moment | null => (date ? moment(date) : null);
const toMomentOnReset = (date?: string | null): Moment | null => (date != null ? moment(date) : null);

// One condition of the date filter popup: its operator and dates as the user edits them, starting from the model.
export const useAgConditionModel = ({ defaultCondition, defaultFromDate, defaultToDate }: Params): AgDateCondition => {
  const [type, setType] = useState<DateFilterOperator>(defaultCondition);
  const [dateFrom, setDateFrom] = useState<PickedDate>(toMoment(defaultFromDate));
  const [dateTo, setDateTo] = useState<PickedDate>(toMoment(defaultToDate));

  const onChange = useCallback((date: PickedDate, isEndDate?: boolean) => {
    const newDate = moment.isMoment(date) ? date.toDate() : date;

    if (isEndDate) {
      setDateTo(newDate);
    } else {
      setDateFrom(newDate);
    }
  }, []);

  const resetToDefault = useCallback(() => {
    setType(defaultCondition);
    setDateFrom(toMomentOnReset(defaultFromDate));
    setDateTo(toMomentOnReset(defaultToDate));
  }, [defaultCondition, defaultFromDate, defaultToDate]);

  useEffect(resetToDefault, [defaultCondition, defaultFromDate, defaultToDate, resetToDefault]);

  const isRangeError = type === 'inRange' && dateFrom != null && dateTo == null;
  const isError = isRangeError || dateFrom == null;

  return {
    type,
    setType,
    dateFrom,
    dateTo,
    onChange,
    resetToDefault,
    isRangeError,
    isError,
  };
};
